import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { fetchPseoPage, type PseoPage } from "@/lib/strapi.functions";
import { BUSINESS } from "@/lib/site-data";

const pseoQuery = (slug: string) =>
  queryOptions({
    queryKey: ["pseo-page", slug],
    queryFn: () => fetchPseoPage({ data: { slug } }),
    staleTime: 60_000,
  });

export const Route = createFileRoute("/$slug")({
  loader: async ({ params, context }) => {
    const page = await context.queryClient.ensureQueryData(pseoQuery(params.slug));
    if (!page) throw notFound();
    return { page };
  },
  head: ({ loaderData, params }) => {
    const p = loaderData?.page as PseoPage | undefined;
    if (!p) return { meta: [{ title: "Page not found" }] };
    const title = p.metaTitle ?? `${p.heroHeadline} | ${BUSINESS.shortName}`;
    const description =
      p.metaDescription ?? p.heroSubheadline ?? p.localContext?.slice(0, 155) ?? "";
    const canonical = `https://lehitradessaeo.com/${params.slug}`;
    const meta = [
      { title },
      { name: "description", content: description },
      // Open Graph
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: canonical },
      { property: "og:site_name", content: BUSINESS.name },
      // Twitter
      { name: "twitter:card", content: p.ogImage ? "summary_large_image" : "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ];
    if (p.ogImage) {
      meta.push(
        { property: "og:image", content: p.ogImage },
        { property: "og:image:alt", content: p.heroHeadline },
        { name: "twitter:image", content: p.ogImage },
      );
    }
    return {
      meta,
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: p.heroHeadline,
            description,
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
            url: canonical,
            ...(p.ogImage ? { image: [p.ogImage] } : {}),
            ...(p.updatedAt
              ? { datePublished: p.updatedAt, dateModified: p.updatedAt }
              : {}),
            author: {
              "@type": "Organization",
              "@id": "https://lehitradessaeo.com/#organization",
              name: BUSINESS.name,
            },
            publisher: { "@id": "https://lehitradessaeo.com/#organization" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://lehitradessaeo.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: p.heroHeadline,
                item: canonical,
              },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-3xl font-black">Page not found</h1>
      <p className="mt-2 text-muted-foreground">
        <Link to="/" className="underline">Back home</Link>
      </p>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-3xl font-black">Couldn't load this page</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </main>
  ),
  component: PseoPageView,
});

function Prose({ text }: { text: string }) {
  return (
    <>
      {text.split(/\n\n+/).map((para, i) => (
        <p key={i} className="mt-5 text-lg leading-relaxed text-muted-foreground">
          {para}
        </p>
      ))}
    </>
  );
}

function PseoPageView() {
  const { slug } = Route.useParams();
  const { data: page } = useSuspenseQuery(pseoQuery(slug));
  if (!page) return null;

  return (
    <main className="mx-auto max-w-3xl px-6 py-20">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {BUSINESS.shortName} · Local Trades SEO
        </p>
        <h1 className="mt-4 font-display text-4xl tracking-tight md:text-5xl">
          {page.heroHeadline}
        </h1>
        {page.heroSubheadline && (
          <p className="mt-5 text-xl leading-relaxed text-muted-foreground">
            {page.heroSubheadline}
          </p>
        )}
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {page.ctaText ?? "Get my free audit"}
          </Link>
          <a
            href={`tel:${BUSINESS.phone}`}
            className="inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-accent"
          >
            Call {BUSINESS.phone}
          </a>
        </div>
      </header>

      {page.localContext && (
        <section className="mt-16">
          <h2 className="font-display text-2xl tracking-tight">Why this matters locally</h2>
          <Prose text={page.localContext} />
        </section>
      )}

      {page.technicalExplanation && (
        <section className="mt-16">
          <h2 className="font-display text-2xl tracking-tight">How we deliver it</h2>
          <Prose text={page.technicalExplanation} />
        </section>
      )}

      <div className="mt-20 rounded-2xl border border-border bg-card p-8 text-center">
        <h3 className="font-display text-2xl tracking-tight">
          Ready to dominate your local market?
        </h3>
        <p className="mt-3 text-muted-foreground">
          Free local SEO audit. No pitch deck, no contract pressure.
        </p>
        <Link
          to="/contact"
          className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {page.ctaText ?? "Get my free audit"}
        </Link>
      </div>
    </main>
  );
}
